// src/components/learning/Confetti.js
// Animations de célébration pour les exercices

import React, { useEffect, useState } from 'react';

const Confetti = ({ type = 'confetti', duration = 3000, onComplete }) => {
  const [particles, setParticles] = useState([]);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    // Générer les particules
    const colors = type === 'gold' 
      ? ['#FFD700', '#FFA500', '#FFD700', '#FFED4A', '#F59E0B']
      : ['#667eea', '#764ba2', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899'];
    
    const newParticles = [];
    const count = type === 'fireworks' ? 100 : 50;

    for (let i = 0; i < count; i++) {
      newParticles.push({
        id: i,
        x: Math.random() * 100,
        y: type === 'fireworks' ? 50 : -10,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 10 + 5,
        speedX: (Math.random() - 0.5) * (type === 'fireworks' ? 8 : 3),
        speedY: Math.random() * 3 + 2,
        rotation: Math.random() * 360,
        rotationSpeed: (Math.random() - 0.5) * 10,
        shape: Math.random() > 0.5 ? 'circle' : 'square'
      });
    }
    setParticles(newParticles);

    // Animation
    const interval = setInterval(() => {
      setParticles(prev => prev.map(p => ({
        ...p,
        x: p.x + p.speedX * 0.5,
        y: p.y + p.speedY,
        rotation: p.rotation + p.rotationSpeed,
        speedY: p.speedY + 0.1 // gravité
      })));
    }, 30);

    // Fin de l'animation
    const timeout = setTimeout(() => {
      setVisible(false);
      clearInterval(interval);
      if (onComplete) onComplete();
    }, duration);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [type, duration, onComplete]);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      pointerEvents: 'none',
      zIndex: 10000,
      overflow: 'hidden'
    }}>
      {particles.map(p => (
        <div
          key={p.id}
          style={{
            position: 'absolute',
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            borderRadius: p.shape === 'circle' ? '50%' : '2px',
            transform: `rotate(${p.rotation}deg)`,
            opacity: Math.max(0, 1 - p.y / 150),
            boxShadow: type === 'gold' ? `0 0 6px ${p.color}` : 'none'
          }}
        />
      ))}
    </div>
  );
};

// Composant étoile animée pour les bonnes réponses
export const StarBurst = ({ onComplete }) => {
  const [scale, setScale] = useState(0);
  
  useEffect(() => {
    setScale(1);
    const timeout = setTimeout(() => {
      setScale(0);
      if (onComplete) onComplete();
    }, 800);
    return () => clearTimeout(timeout);
  }, [onComplete]);

  return (
    <div style={{
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: `translate(-50%, -50%) scale(${scale})`,
      fontSize: '8rem',
      transition: 'transform 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
      zIndex: 10001,
      pointerEvents: 'none',
      textShadow: '0 0 30px rgba(255, 215, 0, 0.8)'
    }}>
      ⭐
    </div>
  );
};

// Composant trophée pour score parfait
export const Trophy = ({ onComplete }) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setVisible(false);
      if (onComplete) onComplete();
    }, 4000);
    return () => clearTimeout(timeout);
  }, [onComplete]);

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      zIndex: 10002,
      animation: 'fadeIn 0.5s ease'
    }}>
      <div style={{
        textAlign: 'center',
        animation: 'bounceIn 0.6s ease'
      }}>
        <div style={{
          fontSize: '10rem',
          animation: 'pulse 1s ease infinite',
          textShadow: '0 0 50px rgba(255, 215, 0, 0.8)'
        }}>
          🏆
        </div>
        <div style={{
          fontSize: '2.5rem',
          color: '#FFD700',
          fontWeight: 'bold',
          marginTop: '1rem',
          textShadow: '0 2px 10px rgba(0,0,0,0.5)'
        }}>
          PARFAIT !
        </div>
        <div style={{
          fontSize: '1.5rem',
          color: 'white',
          marginTop: '0.5rem'
        }}>
          100% de bonnes réponses !
        </div>
      </div>
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes bounceIn {
          0% { transform: scale(0); }
          50% { transform: scale(1.2); }
          100% { transform: scale(1); }
        }
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
};

export default Confetti;
